<script setup lang="ts">
import { usePlaceMenuStore } from "@/modules/place-module";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useInterval, useWindowSize } from "@vueuse/core";

const placeMenuStore = usePlaceMenuStore();
const product = computed(() => placeMenuStore.product);
const selectImage = ref(0);
const { width } = useWindowSize();
const { counter, reset, pause, resume } = useInterval(100, { controls: true });

const progress = computed(() => {
  return (counter.value / 40) * 100;
});

watch(counter, (newValue) => {
  if (newValue === 41) {
    reset();

    if (selectImage.value === product?.value.product.images.length - 1) {
      selectImage.value = 0;
      return;
    }

    selectImage.value++;
  }
});

watch(width, (newValue) => {
  if (newValue < 1024) {
    resume();
    return;
  }

  pause();
});

onBeforeMount(() => {
  if (width.value > 1023) {
    pause();
  }
});
</script>

<template>
  <div v-if="width > 1023" class="product-images">
    <div v-if="product?.product.images.length" class="product-images__wrapper">
      <div
        class="product-image"
        :style="{
          backgroundImage: `url(${product?.product.images[selectImage]})`,
        }"
      />
      <div class="product-images__thumbs">
        <button
          v-for="(image, index) in product?.product.images"
          :key="index"
          type="button"
          :class="{ active: index === selectImage }"
          @click="selectImage = index"
        >
          <span
            :style="{
              backgroundImage: `url(${image})`,
            }"
          />
        </button>
      </div>
    </div>

    <div v-else class="product-images__empty">
      <img src="@/assets/images/no-photo.svg" alt="no-photo" />
    </div>
  </div>

  <div v-else class="product-slider">
    <div
      class="product-slider__slide"
      :style="{
        backgroundImage: product?.product.images.length
          ? `url(${product?.product.images[selectImage]})`
          : '',
      }"
    />

    <div
      v-if="product?.product.images.length > 1"
      class="product-slider__controls"
    >
      <div
        v-for="(slide, index) in product?.product.images"
        :key="index"
        class="product-slider__control"
      >
        <span>
          <span
            v-if="selectImage === index"
            :style="{ width: `${progress}%` }"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-images {
  margin: calc($base-space * 6) 0 0;
  padding: 0 calc($base-space * 8);

  &__wrapper {
    height: 336px;
    display: grid;
    grid-template-columns: 1fr 64px;
    gap: calc($base-space * 4);
  }

  &__thumbs {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;

    > button {
      width: 64px;
      height: 64px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.active {
        border: 2px solid var(--color-semantic-border-other-selected);
      }

      > span {
        width: 56px;
        height: 56px;
        border-radius: 4px;
        @include centerBackgroundImageCover();
      }
    }
  }

  &__empty {
    width: 100%;
    height: 336px;
    background-color: var(--color-semantic-background-normal-bg-secondary);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    > p {
      max-width: 120px;
      @include body-bold-l();
      color: var(--color-semantic-content-normal-tertiary);
      text-align: center;
    }
  }
}

.product-image {
  border-radius: 16px;
  @include centerBackgroundImageCover();
}

.product-slider {
  margin: calc($base-space * 5) 0 0;
  width: 100%;
  padding: 0 calc($base-space * 6);

  @media (max-width: 767px) {
    padding: 0 calc($base-space * 4);
    margin: calc($base-space * 4) 0 0;
  }

  &__controls {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: calc($base-space * 2) 0 0;
    padding: 0 calc($base-space * 4);

    > a {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 0 0 calc($base-space * 4);

      > .icon {
        background-color: var(--color-semantic-content-normal-contrast-primary);
      }
    }
  }

  &__control {
    padding: calc($base-space * 2);
    width: 100%;
    position: relative;

    > span {
      height: 4px;
      border-radius: 4px;

      background-color: var(--color-semantic-background-normal-tertiary);
      width: 100%;
      position: relative;
      display: block;

      > span {
        display: block;
        position: absolute;
        left: 0;
        bottom: 0;
        top: 0;
        border-radius: 20px;
        background-color: var(--color-semantic-background-normal-contrast);
        transition: 0.1s ease-in;
      }
    }
  }

  &__slide {
    position: relative;
    aspect-ratio: 1/1;
    @include centerBackgroundImageCover();
    border-radius: 16px;
  }
}
</style>
